/* ──────────────────────────────────────────────────────────────────────────
 * Ingestion adapters (brief §10) — government data is optional.
 *
 * The platform must not fail because a department database is unavailable.
 * Every adapter here turns an independent source into the same internal
 * record shape the map already consumes, so a department integration added
 * later is one more adapter rather than a redesign.
 *
 * Supported today, client-side, with no backend:
 *   GeoJSON · CSV · KML · GPX
 *
 * Declared but not implemented (they need a server, and saying otherwise
 * would be a lie the architecture has to live with):
 *   approved APIs · authorised sensor feeds · authorised CCTV analytics
 *
 * PROVENANCE IS MANDATORY. Every adapter stamps imported records with their
 * real origin — filename, format, import time — and marks them UNVERIFIED,
 * never VERIFIED. An operator importing a file has asserted nothing about
 * its accuracy; only a named authority can do that, and this POC has no
 * mechanism for one. Defaulting to VERIFIED would let an unchecked
 * spreadsheet inherit the authority of a survey.
 * ────────────────────────────────────────────────────────────────────────── */

import { withProvenance, TRUST, SOURCE_KIND, CLASSIFICATION } from "../data/provenance";

export const FORMATS = {
  GEOJSON: "geojson",
  CSV: "csv",
  KML: "kml",
  GPX: "gpx",
};

/* Detect by extension, then by sniffing content — a department file is as
 * likely to arrive as "export.txt" as with a correct extension. */
export function detectFormat(filename = "", text = "") {
  const ext = filename.toLowerCase().split(".").pop();
  if (ext === "geojson" || ext === "json") return FORMATS.GEOJSON;
  if (ext === "csv" || ext === "tsv") return FORMATS.CSV;
  if (ext === "kml") return FORMATS.KML;
  if (ext === "gpx") return FORMATS.GPX;

  const head = text.slice(0, 400).trim();
  if (head.startsWith("{") && head.includes("Feature")) return FORMATS.GEOJSON;
  if (head.includes("<gpx")) return FORMATS.GPX;
  if (head.includes("<kml") || head.includes("<Placemark")) return FORMATS.KML;
  if (head.includes(",") && head.split("\n").length > 1) return FORMATS.CSV;
  return null;
}

function importProvenance(meta) {
  return {
    source: meta.source || SOURCE_KIND.GEOJSON,
    trust: TRUST.UNVERIFIED,
    confidence: null,
    owner: meta.owner || "Imported by operator",
    classification: meta.classification || CLASSIFICATION.INTERNAL,
    observedAt: null,
    updatedAt: new Date().toISOString(),
    note:
      `Imported from "${meta.filename}" at ${new Date().toLocaleString()}. ` +
      "Marked UNVERIFIED: import is not verification. A named authority must " +
      "confirm these records before they inform an operational decision.",
  };
}

/* ── GeoJSON ───────────────────────────────────────────────────────────── */
export function parseGeoJSON(text, meta = {}) {
  const errors = [];
  let doc;
  try {
    doc = JSON.parse(text);
  } catch (e) {
    return { ok: false, errors: [`Not valid JSON: ${e.message}`], records: [] };
  }

  const features =
    doc.type === "FeatureCollection" ? doc.features
    : doc.type === "Feature" ? [doc]
    : null;

  if (!features) {
    return { ok: false, errors: ["Not a GeoJSON Feature or FeatureCollection"], records: [] };
  }

  const prov = importProvenance({ ...meta, source: SOURCE_KIND.GEOJSON });
  const records = [];

  features.forEach((f, i) => {
    const g = f.geometry;
    if (!g) { errors.push(`Feature ${i}: no geometry, skipped`); return; }
    const props = f.properties || {};
    const name = props.name || props.NAME || props.title || `Imported ${i + 1}`;
    const base = { id: props.id || `IMP-${i + 1}`, name: { en: name, mr: name }, raw: props };

    if (g.type === "Point") {
      records.push(withProvenance({ ...base, kind: "point", coord: g.coordinates }, prov));
    } else if (g.type === "LineString") {
      records.push(withProvenance({ ...base, kind: "line", coords: g.coordinates }, prov));
    } else if (g.type === "Polygon") {
      records.push(withProvenance({ ...base, kind: "polygon", polygon: g.coordinates[0] }, prov));
    } else if (g.type === "MultiPolygon") {
      g.coordinates.forEach((poly, j) =>
        records.push(withProvenance(
          { ...base, id: `${base.id}-${j + 1}`, kind: "polygon", polygon: poly[0] }, prov)));
    } else {
      errors.push(`Feature ${i}: geometry "${g.type}" not supported, skipped`);
    }
  });

  return { ok: records.length > 0, errors, records, format: FORMATS.GEOJSON };
}

/* ── CSV ───────────────────────────────────────────────────────────────
 * Minimal RFC-4180-ish reader: handles quoted fields and embedded commas,
 * which is where naive split(",") implementations corrupt department data. */
function splitCsvLine(line, delim) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i += 1; }
      else if (ch === '"') inQuotes = false;
      else cur += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === delim) { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

const LAT_KEYS = ["lat", "latitude", "y", "lat_dd"];
const LNG_KEYS = ["lng", "lon", "long", "longitude", "x", "lon_dd"];

export function parseCSV(text, meta = {}) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
  if (lines.length < 2) {
    return { ok: false, errors: ["CSV needs a header row and at least one data row"], records: [] };
  }

  const delim = lines[0].includes("\t") ? "\t" : ",";
  const header = splitCsvLine(lines[0], delim).map((h) => h.toLowerCase());

  const latIdx = header.findIndex((h) => LAT_KEYS.includes(h));
  const lngIdx = header.findIndex((h) => LNG_KEYS.includes(h));
  if (latIdx === -1 || lngIdx === -1) {
    return {
      ok: false,
      records: [],
      errors: [
        `No coordinate columns found. Looked for latitude in [${LAT_KEYS.join(", ")}] ` +
        `and longitude in [${LNG_KEYS.join(", ")}]. Header was: ${header.join(", ")}`,
      ],
    };
  }

  const nameIdx = header.findIndex((h) => ["name", "title", "label"].includes(h));
  const idIdx = header.findIndex((h) => ["id", "code", "ref"].includes(h));
  const prov = importProvenance({ ...meta, source: SOURCE_KIND.CSV });

  const errors = [];
  const records = [];
  lines.slice(1).forEach((line, i) => {
    const cells = splitCsvLine(line, delim);
    const lat = Number(cells[latIdx]);
    const lng = Number(cells[lngIdx]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      errors.push(`Row ${i + 2}: coordinates not numeric, skipped`);
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      errors.push(`Row ${i + 2}: coordinates out of range (${lat}, ${lng}) — lat/lng may be swapped, skipped`);
      return;
    }
    const name = nameIdx >= 0 ? cells[nameIdx] : `Imported ${i + 1}`;
    const raw = Object.fromEntries(header.map((h, j) => [h, cells[j]]));
    records.push(withProvenance({
      id: idIdx >= 0 ? cells[idIdx] : `IMP-${i + 1}`,
      name: { en: name, mr: name },
      kind: "point",
      coord: [lng, lat],
      raw,
    }, prov));
  });

  return { ok: records.length > 0, errors, records, format: FORMATS.CSV };
}

/* ── KML / GPX ─────────────────────────────────────────────────────────
 * Parsed with the browser's own DOMParser rather than a dependency. Both are
 * XML; the difference is which elements carry the geometry. */
function xmlDoc(text) {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) throw new Error("Malformed XML");
  return doc;
}

/* KML coordinates are "lng,lat[,alt]" separated by whitespace — note the
 * order is already lng,lat, unlike most CSV exports. */
function parseKmlCoords(str) {
  return str.trim().split(/\s+/).map((tok) => {
    const [lng, lat] = tok.split(",").map(Number);
    return [lng, lat];
  }).filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b));
}

export function parseKML(text, meta = {}) {
  let doc;
  try { doc = xmlDoc(text); }
  catch (e) { return { ok: false, errors: [e.message], records: [] }; }

  const prov = importProvenance({ ...meta, source: SOURCE_KIND.DEPARTMENT });
  const errors = [];
  const records = [];

  doc.querySelectorAll("Placemark").forEach((pm, i) => {
    const name = pm.querySelector("name")?.textContent?.trim() || `Placemark ${i + 1}`;
    const base = { id: `KML-${i + 1}`, name: { en: name, mr: name } };

    const point = pm.querySelector("Point > coordinates");
    const line = pm.querySelector("LineString > coordinates");
    const poly = pm.querySelector("Polygon outerBoundaryIs coordinates");

    if (point) {
      const c = parseKmlCoords(point.textContent)[0];
      if (c) records.push(withProvenance({ ...base, kind: "point", coord: c }, prov));
    } else if (line) {
      records.push(withProvenance({ ...base, kind: "line", coords: parseKmlCoords(line.textContent) }, prov));
    } else if (poly) {
      records.push(withProvenance({ ...base, kind: "polygon", polygon: parseKmlCoords(poly.textContent) }, prov));
    } else {
      errors.push(`Placemark ${i + 1} ("${name}"): no supported geometry, skipped`);
    }
  });

  return { ok: records.length > 0, errors, records, format: FORMATS.KML };
}

export function parseGPX(text, meta = {}) {
  let doc;
  try { doc = xmlDoc(text); }
  catch (e) { return { ok: false, errors: [e.message], records: [] }; }

  const prov = importProvenance({ ...meta, source: SOURCE_KIND.OPERATOR });
  const errors = [];
  const records = [];

  // Waypoints → points
  doc.querySelectorAll("wpt").forEach((w, i) => {
    const lat = Number(w.getAttribute("lat"));
    const lng = Number(w.getAttribute("lon"));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      errors.push(`Waypoint ${i + 1}: bad coordinates, skipped`); return;
    }
    const name = w.querySelector("name")?.textContent?.trim() || `Waypoint ${i + 1}`;
    records.push(withProvenance({
      id: `GPX-W-${i + 1}`, name: { en: name, mr: name }, kind: "point", coord: [lng, lat],
    }, prov));
  });

  // Tracks and routes → lines
  const lineFrom = (nodes) =>
    Array.from(nodes)
      .map((p) => [Number(p.getAttribute("lon")), Number(p.getAttribute("lat"))])
      .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b));

  doc.querySelectorAll("trk").forEach((trk, i) => {
    const coords = lineFrom(trk.querySelectorAll("trkpt"));
    if (coords.length < 2) { errors.push(`Track ${i + 1}: fewer than 2 points, skipped`); return; }
    const name = trk.querySelector("name")?.textContent?.trim() || `Track ${i + 1}`;
    records.push(withProvenance({
      id: `GPX-T-${i + 1}`, name: { en: name, mr: name }, kind: "line", coords,
    }, prov));
  });

  doc.querySelectorAll("rte").forEach((rte, i) => {
    const coords = lineFrom(rte.querySelectorAll("rtept"));
    if (coords.length < 2) { errors.push(`Route ${i + 1}: fewer than 2 points, skipped`); return; }
    const name = rte.querySelector("name")?.textContent?.trim() || `Route ${i + 1}`;
    records.push(withProvenance({
      id: `GPX-R-${i + 1}`, name: { en: name, mr: name }, kind: "line", coords,
    }, prov));
  });

  return { ok: records.length > 0, errors, records, format: FORMATS.GPX };
}

/* ── Unified entry point ───────────────────────────────────────────────── */
export function ingestText(text, filename = "upload", meta = {}) {
  const format = detectFormat(filename, text);
  const m = { ...meta, filename };
  switch (format) {
    case FORMATS.GEOJSON: return parseGeoJSON(text, m);
    case FORMATS.CSV:     return parseCSV(text, m);
    case FORMATS.KML:     return parseKML(text, m);
    case FORMATS.GPX:     return parseGPX(text, m);
    default:
      return {
        ok: false, records: [],
        errors: [`Could not determine the format of "${filename}". Supported: GeoJSON, CSV, KML, GPX.`],
      };
  }
}

export async function ingestFile(file, meta = {}) {
  const text = await file.text();
  return ingestText(text, file.name, meta);
}

/* ── Adapters declared but not implemented ─────────────────────────────
 * Listed so the integration surface is visible and so nobody has to guess
 * what a backend must provide. Each throws rather than returning empty
 * data — silently returning nothing would look like "no records found"
 * instead of "not built". */
export const PENDING_ADAPTERS = [
  { key: "api",    label: "Approved government API",    needs: "Backend proxy holding credentials (§11)" },
  { key: "sensor", label: "Authorised sensor feed",     needs: "Streaming endpoint + device authentication" },
  { key: "cctv",   label: "Authorised CCTV analytics",  needs: "Detector pipeline emitting the Vision event contract" },
];

export function connectPendingAdapter(key) {
  const a = PENDING_ADAPTERS.find((x) => x.key === key);
  throw new Error(
    `Adapter "${key}" is not implemented in this POC. ${a ? `Requires: ${a.needs}` : ""}`.trim()
  );
}
